import React, { useCallback, useState } from "react";
import { Send } from "lucide-react";
import { useParams, useNavigate } from "react-router-dom";
import Services from "../lib/services";
import api from "../lib/api";

const WalletTransfer = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [senderName, setSenderName] = useState("");
  const [senderContact, setSenderContact] = useState("");
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleMouseEnter = useCallback((e) => {
    e.currentTarget.style.background = "rgba(255,255,255,0.35)";
  }, []);

  const handleMouseLeave = useCallback((e) => {
    e.currentTarget.style.background = "rgba(255,255,255,0.2)";
  }, []);

  const Header = ({ title }) => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        marginBottom: "20px",
        padding: "8px 0",
      }}
    >
      <div
        onClick={() => navigate(-1)}
        style={{
          width: "40px",
          height: "40px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "rgba(255,255,255,0.9)",
          borderRadius: "12px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
          marginRight: "10px",
          cursor: "pointer",
        }}
      >
        <i
          className="fi fi-br-angle-left"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          style={{ fontSize: "20px", color: "#555" }}
        ></i>
      </div>
      <h2 style={{ fontSize: "1.4rem", fontWeight: 700, color: "#2D3436" }}>
        {title}
      </h2>
    </div>
  );

  const handleSubmit = async () => {
    if (!senderName || !senderContact || !amount || !method) {
      alert("⚠️ Please fill in all fields.");
      return;
    }

    const body = {
      receiverId: id,
      amount: Number(amount),
      senderName,
      senderContact,
      paymentMethod: method,
    };

    try {
      setIsSubmitting(true);
      const response = await Services.wallet.topup(body);

      console.log("Payment response:", response);

      alert("✅ Transfer request submitted successfully!");
      setSenderName("");
      setSenderContact("");
      setAmount("");
      setMethod("");

      // Log activity
      try {
        await api.post('/api/activities/add-activity', { message: 'Topped up wallet' });
      } catch (err) {
        console.error('Failed to log topup activity', err);
      }
    } catch (error) {
      console.error("Transfer failed:", error);
      alert("❌ Failed to process transfer. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      style={{
        maxWidth: "450px",
        margin: "0 auto",
        padding: "20px",
        fontFamily: "'Inter', sans-serif",
      }}
    >
      <Header title="Wallet Transfer" />

      {/* Small Note */}
      <p
        style={{
          fontSize: "15px",
          color: "#555",
          marginBottom: "24px",
          background: "#f8f9fa",
          padding: "12px 14px",
          borderRadius: "12px",
          lineHeight: "1.5",
        }}
      >
        💡 <strong>Note:</strong> Enter your details, the amount to transfer, and
        select a payment method.
      </p>

      {/* Form */}
      <div
        style={{
          background: "#fff",
          borderRadius: "16px",
          padding: "20px",
          boxShadow: "0 4px 10px rgba(0,0,0,0.06)",
        }}
      >
        {/* Sender Name */}
        <label style={labelStyle}>Sender Name</label>
        <input
          type="text"
          value={senderName}
          onChange={(e) => setSenderName(e.target.value)}
          placeholder="Enter your full name"
          style={inputStyle}
        />

        {/* Sender Contact */}
        <label style={labelStyle}>Sender Contact</label>
        <input
          type="text"
          value={senderContact}
          onChange={(e) => setSenderContact(e.target.value)}
          placeholder="Enter your phone or email"
          style={inputStyle}
        />

        {/* Amount */}
        <label style={labelStyle}>Amount</label>
        <input
          type="number"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="Enter amount"
          style={inputStyle}
        />

        {/* Payment Method */}
        <label style={labelStyle}>Payment Method</label>
        <select
          value={method}
          onChange={(e) => setMethod(e.target.value)}
          style={inputStyle}
        >
          <option value="">Select method</option>
          <option value="bank">Bank Transfer</option>
          <option value="wallet">Wallet Balance</option>
          <option value="card">Debit Card</option>
        </select>

        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={isSubmitting}
          style={{
            width: "100%",
            background: "linear-gradient(135deg, #9333ea, #6b21a8)",
            color: "white",
            border: "none",
            borderRadius: "12px",
            padding: "14px 0",
            fontSize: "16px",
            fontWeight: "600",
            cursor: isSubmitting ? "not-allowed" : "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            opacity: isSubmitting ? 0.7 : 1,
            transition: "all 0.3s ease",
          }}
        >
          <Send size={18} />
          {isSubmitting ? "Processing..." : "Submit Transfer"}
        </button>
      </div>
    </div>
  );
};

// Reusable styles
const labelStyle = {
  display: "block",
  marginBottom: "8px",
  fontWeight: "500",
  color: "#444",
};

const inputStyle = {
  width: "100%",
  padding: "12px",
  borderRadius: "10px",
  border: "1px solid #ddd",
  marginBottom: "16px",
  fontSize: "15px",
  background: "white",
};

export default WalletTransfer;
